import { useState } from 'react'

import {
  Headline,
  Icon,
  LoadingModal,
  ModalProvider,
  SecondaryButton,
} from '@joor/design-system'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

import usePortal from 'hooks/usePortal'
import { useToasts } from 'hooks/useToasts'
import { isLiteRetailer as isLiteRetailerSelector } from 'store/currentUser/selectors'

import ErrorPage from 'pages/ErrorPage'

import ErrorState from './components/ErrorState'
import { FeatureCard } from './components/FeatureCard/index'
import { useCustomerPortalLink } from './mutations.hooks'
import { useGetFeatures } from './queries.hooks'
import { SubscriptionsPageIds } from './subscriptions.ids'
import { sortPremiumFeatures } from './utils'

// TODO: implement this fix at the DS level
const FixedSecondaryButton = styled(SecondaryButton)`
  svg circle {
    stroke: var(--color-primary-500);
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px 16px 20px;
`

const StyledHeadline = styled(Headline)`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ButtonContentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 20px;
`

const SubscriptionsPage = () => {
  const [params] = useSearchParams()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const Portal = usePortal()
  const featureId = params.get('featureId')
    ? decodeURIComponent(params.get('featureId')!)
    : undefined

  const { data, loading, error } = useGetFeatures()
  const { showErrorToast } = useToasts()
  const { createCustomerPortalLink, loading: buttonLoading } =
    useCustomerPortalLink()

  const canAccessCustomerPortal = data?.premiumFeatures?.some((feat) =>
    feat?.subscriptions?.some((sub) => sub?.isOwner),
  )

  const premiumFeatures = [...(data?.premiumFeatures || [])]

  const premiumFeaturesWithPrices = premiumFeatures.filter(
    (pf) => pf.prices && pf.prices?.length > 0,
  )

  const premiumFeaturesWithPackageGroup = premiumFeaturesWithPrices.map(
    (feature) => ({
      ...feature,
      packagesStatus: feature?.groupId
        ? premiumFeaturesWithPrices
            ?.filter((ft) => ft.groupId === feature.groupId)
            .map((x) => ({
              id: x.id,
              status: x?.subscriptionsView?.current?.status,
            }))
        : undefined,
    }),
  )

  const sortedPremiumFeatures = premiumFeaturesWithPackageGroup?.sort((a, b) =>
    sortPremiumFeatures(
      { id: a.id, name: a.name, status: a?.subscriptionsView?.current?.status },
      { id: b.id, name: b.name, status: b.subscriptionsView.current?.status },
      featureId,
    ),
  )

  const handleCustomerPortalClick = async () => {
    try {
      setIsOpen(true)
      const res = await createCustomerPortalLink()
      if (res?.data?.createCustomerPortalLink) {
        window.location.assign(res.data.createCustomerPortalLink)
      } else {
        throw new Error()
      }
    } catch (err) {
      setIsOpen(false)
      showErrorToast(
        'An error occurred while redirecting to the customer portal. Please try again.',
      )
    }
  }

  if (loading) {
    return null
  }

  if (error) {
    return (
      <div>
        <Header>
          <StyledHeadline type="h4">
            Premium Features <Icon plain iconName="unlock" />
          </StyledHeadline>
        </Header>
        <hr
          style={{
            border: '0.5px solid var(--color-primary-200)',
            marginBottom: 80,
          }}
        />

        <ErrorState />
      </div>
    )
  }

  return (
    <div>
      <Header>
        <StyledHeadline type="h4">
          Premium Features <Icon plain iconName="unlock" />
        </StyledHeadline>
        {canAccessCustomerPortal && (
          <FixedSecondaryButton
            id={SubscriptionsPageIds.CustomerPortal}
            onClick={handleCustomerPortalClick}
            loading={buttonLoading}
          >
            <ButtonContentWrapper>
              <Icon plain iconSize="small" iconName="link-out" />{' '}
              <span>Manage Billing & Subscriptions</span>
            </ButtonContentWrapper>
          </FixedSecondaryButton>
        )}
      </Header>
      <hr style={{ border: '0.5px solid var(--color-primary-200)' }} />
      <CardContainer>
        {sortedPremiumFeatures?.map((feature, index) => (
          <FeatureCard
            key={feature.id}
            feature={feature}
            index={index}
            setIsOpen={setIsOpen}
          />
        ))}
      </CardContainer>
      <Portal>
        <ModalProvider>
          {isOpen && (
            <LoadingModal
              title={{ text: 'Please wait while we redirect you to Stripe.' }}
              onClose={() => {}}
              disableBackgroundClick={true}
              isOpen={isOpen}
            />
          )}
        </ModalProvider>
      </Portal>
    </div>
  )
}

function SubscriptionsPageWrapper() {
  const isLiteRetailer = useSelector(isLiteRetailerSelector)

  if (!isLiteRetailer) return <ErrorPage />

  return <SubscriptionsPage />
}

export default SubscriptionsPageWrapper
